<template>
  <div :class="[$options.name, statusClassList]" :data-e-ga-id="uniqueId">
    <div v-if="label || $slots.tooltip" class="tw:flex tw:gap-1 tw:items-center">
      <label v-if="label" :class="['tw:body-1 ', $options.name + '__label']" :for="$options.name + '_' + id">
        {{ label }}
      </label>
      <!--  @slot This slot is for the tooltip component.  -->
      <slot name="tooltip" />
    </div>
    <div :class="$options.name + '__inner-wrapper'">
      <div :class="$options.name + '__input-wrapper'">
        <div
          v-if="type !== 'currency' && (type === 'password' || !!$slots.icon)"
          :class="$options.name + '__icon'"
          @click="inputRef?.focus()"
        >
          <!--  @slot This slot is for icon to the left of the input. Only for default type.  -->
          <slot v-if="type !== 'password'" name="icon"></slot>
          <font-awesome-icon v-else icon="fa-light fa-lock-keyhole" />
        </div>
        <input
          :id="`${name || $options.name}_${id || uniqueId}`"
          ref="inputRef"
          v-model="internalValue"
          :disabled="disabled"
          :maxlength="getMaxlength ?? undefined"
          :placeholder="placeholder"
          :type="inputType"
          :autocomplete="autocomplete"
          @blur="handleBlur"
          @change="handleChange"
          @input="handleInput($event)"
          @keydown="handleKeydown($event)"
          @keydown.enter="$emit('enter', internalValue)"
        />
        <div v-if="type === 'currency'" :class="$options.name + '__icon-currency'">
          {{ currency }}
        </div>
        <template v-if="internalValue && clearable">
          <font-awesome-icon
            v-if="type !== 'password'"
            :class="$options.name + '__icon-clear'"
            :data-e-ga-id="uniqueId + '_clear'"
            icon="fa-light fa-xmark"
            @click="clearValue"
          />
          <template v-else>
            <font-awesome-icon
              v-if="inputType !== 'password'"
              :class="$options.name + '__icon-password'"
              :data-e-ga-id="uniqueId + '_reveal-password'"
              icon="fa-light fa-eye-slash"
              @click="revealPassword"
            />
            <font-awesome-icon
              v-else
              :class="$options.name + '__icon-password'"
              :data-e-ga-id="uniqueId + '_reveal-password'"
              icon="fa-light fa-eye"
              @click="revealPassword"
            />
          </template>
        </template>
      </div>
      <EdroneTooltip
        v-if="showCopyButton"
        :offset="[0, 8]"
        :unique-id="uniqueId + '_copy-input-button'"
        close-trigger-class-name="copied_confirmation"
        position="top"
        trigger-event="click"
        type="custom"
      >
        <div :class="$options.name + '__clone-wrapper'" @click="handleClickCopy(internalValue)">
          <font-awesome-icon :class="$options.name + '__icon-clone'" icon="fa-light fa-clone" />
        </div>
        <template #custom>
          <span class="copied_confirmation tw:caption tw:!text-default-700">{{ copied + " 😊" }}</span>
        </template>
      </EdroneTooltip>
    </div>
    <div v-if="showHintContainer" class="tw:flex tw:justify-between">
      <p v-if="!disabled && invalid && errorMsg" class="tw:caption tw:!text-red-500">
        {{ errorMsg }}
      </p>
      <p v-else-if="helper" class="tw:caption tw:!text-default-700">{{ helper }}</p>
      <p v-if="getMaxlength" class="tw:caption tw:!text-default-700 ms-auto tw:my-0">
        {{ internalValue.toString().length }}/{{ getMaxlength }}
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, PropType, Ref, ref, watch } from "vue";
import EdroneTooltip from "edrone-components/EdroneTooltip.vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";
import { handleClickCopy, copied } from "edrone-components/EdroneInput.helpers.ts";

export default defineComponent({
  name: "EdroneInput",
  components: { EdroneTooltip },
  props: {
    /**
     * Sets the autocomplete attribute for the input element.
     */
    autocomplete: {
      type: String,
      default: undefined,
    },
    type: {
      type: String as PropType<"default" | "password" | "currency" | "number" | "email">,
      default: "default",
    },
    label: {
      type: String,
      default: "",
    },
    helper: {
      type: String,
      default: "",
    },
    errorMsg: {
      type: String,
      default: "",
    },
    value: {
      type: [String, Number],
      required: false,
      default: "",
    },
    modelValue: {
      type: [String, Number],
      required: false,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    /**
     * This prop does not affect the button for copying the value from the input, only whether the input is disabled.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    /**
     * For currency type only. Symbol of currency in input.
     */
    currency: {
      type: String,
      default: "PLN",
    },
    /**
     * Displays the Button for copy value from an input.
     * Whenever you click on the button, the value from the input is copied to the clipboard.
     */
    showCopyButton: {
      type: Boolean,
      default: false,
    },
    /**
     * maxlength of letters in input (number of letters entered is shown below the input)
     */
    maxlength: {
      type: Number,
      default: null,
    },
    uniqueId: {
      type: String,
      default: "",
    },
    /**
     * auto focus input after rendering it
     */
    focused: {
      type: Boolean,
      default: false,
    },
    /**
     *  If true, the value will be controlled.
     */
    controlled: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      default: "",
    },
    /**
     * If false, the clear (x) button will not be shown and value cannot be cleared.
     */
    clearable: {
      type: Boolean,
      default: true,
    },
  },
  emits: {
    /**
     * Triggers at every value change (single char input)
     */
    input: (value: string) => value,
    /**
     * Triggers at every value change (single char input)
     */
    "update:modelValue": (value: string | number) => value,
    /**
     * Triggers when you click Enter on keyboard
     */
    enter: (value: string | number) => value,
    /**
     * Triggers when you change the value and click outside the input
     */
    change: (value: string | number) => typeof value === "string",
    /**
     * Triggers when you click outside the input
     */
    blur: (value: string | number) => typeof value === "string",
  },
  setup(props, { emit }) {
    const internalValue = ref(props.value || props.modelValue);
    const internalInvalid = ref(props.invalid);

    const inputRef: Ref<HTMLInputElement | null> = ref(null);
    onMounted(() => {
      if (props.focused) {
        inputRef.value?.focus();
      }
    });

    const statusClassList = computed(() => {
      return {
        "EdroneInput--default": props.type === "default",
        "EdroneInput--number": props.type === "number",
        "EdroneInput--password": props.type === "password",
        "EdroneInput--currency": props.type === "currency",
        "EdroneInput--email": props.type === "email",
        "EdroneInput--disabled": props.disabled,
        "EdroneInput--invalid": internalInvalid.value || props.invalid,
        "EdroneInput--filled": internalValue.value,
      };
    });

    const inputType = ref(getDefaultType());

    const showHintContainer = computed(() => {
      return (
        (!props.invalid && props.helper) || (!props.disabled && props.invalid && props.errorMsg) || props.maxlength > 0
      );
    });

    watch(
      () => props.type,
      () => {
        inputType.value = getDefaultType();
      }
    );

    watch(
      () => props.value,
      () => {
        internalValue.value = props.value;
      }
    );

    watch(
      () => props.invalid,
      () => {
        internalInvalid.value = props.invalid;
      }
    );

    watch(
      () => internalValue.value,
      () => {
        if (getMaxlength.value && internalValue.value.toString().length > getMaxlength.value) {
          internalInvalid.value = true;
          internalValue.value = internalValue.value.toString().slice(0, props.maxlength);
        }
      }
    );

    function getDefaultType() {
      if (props.type === "password") {
        return "password";
      } else if (props.type === "currency" || props.type === "number") {
        return "number";
      } else if (props.type === "email") {
        return "email";
      } else {
        return "text";
      }
    }

    function clearValue() {
      if (!props.clearable) return;
      internalValue.value = "";
      emit("update:modelValue", "");
      emit("input", "");
      emit("change", "");
      gtagUniversalSend({ component_id: props.uniqueId + "_clear" });
    }

    const getMaxlength = computed(() => {
      if (props.maxlength <= 0) return null;
      return props.maxlength;
    });

    function handleInput(event: Event) {
      if (event instanceof InputEvent && event.inputType === "insertFromPaste") {
        let value = (event.target as HTMLInputElement).value;
        if (props.maxlength && value.length > props.maxlength) {
          value = value.slice(0, props.maxlength);
        }

        props.controlled ? (internalValue.value = props.value) : (internalValue.value = value);
        emit("update:modelValue", internalValue.value);
        emit("change", internalValue.value);
        emit("input", internalValue.value.toString());
      } else {
        props.controlled
          ? (internalValue.value = props.value)
          : (internalValue.value = (event.target as HTMLInputElement).value);

        emit("update:modelValue", internalValue.value);
        emit("input", internalValue.value.toString());
      }
    }

    function handleKeydown(event: KeyboardEvent) {
      if (event.key === "Backspace") {
        internalInvalid.value = false;
        return;
      }
      if (getMaxlength.value && internalValue.value.toString().length === getMaxlength.value) {
        internalInvalid.value = true;
      }
      currencyInputHandler(event);
    }

    function currencyInputHandler(event: KeyboardEvent) {
      if (props.type !== "currency") return;
      const regex = new RegExp(/^\d*\.?\d*$/);
      if (event.ctrlKey || event.metaKey) {
        return;
      }
      // check if the pressed key is a decimal number and if the input length is not longer than maxlength
      if (
        !regex.test(event.key) ||
        (getMaxlength.value && internalValue.value.toString().length >= getMaxlength.value)
      ) {
        event.preventDefault();
      }
    }

    function handleChange() {
      internalInvalid.value = false;
      emit("change", internalValue.value);
    }

    function handleBlur() {
      internalInvalid.value = false;
      emit("blur", internalValue.value);
    }

    function revealPassword() {
      if (props.type !== "password") return;
      gtagUniversalSend({ component_id: props.uniqueId + "_reveal-password" });
      if (inputType.value === "password") {
        inputType.value = "text";
      } else {
        inputType.value = "password";
      }
    }

    return {
      copied,
      props,
      internalValue,
      inputType,
      statusClassList,
      getMaxlength,
      clearValue,
      handleClickCopy,
      handleInput,
      handleChange,
      handleBlur,
      handleKeydown,
      revealPassword,
      showHintContainer,
      inputRef,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.EdroneInput {
  display: inline-flex;
  flex-direction: column;
  width: 100%;
  @apply tw:gap-1;
}

.EdroneInput__label {
  text-overflow: ellipsis;
  max-width: 100%;
  overflow: hidden;
}

.EdroneInput__inner-wrapper {
  display: inline-flex;
  flex-direction: row;
  width: 100%;
  position: relative;
  @apply tw:gap-2;
}

.EdroneInput__inner-wrapper .EdroneInput__clone-wrapper {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  user-select: none;
  @apply tw:border tw:border-default-200;
}

.EdroneInput__inner-wrapper .EdroneInput__clone-wrapper:hover {
  @apply tw:border tw:border-default-400;
}

.EdroneInput__input-wrapper {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: fit-content;
  position: relative;
  @apply tw:text-sm tw:leading-5;
}

.EdroneInput input:-webkit-autofill,
.EdroneInput input:-webkit-autofill:hover,
.EdroneInput input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
}

.EdroneInput input {
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
  padding: 10px 12px;
  border-radius: 4px;
  width: 100%;
  @apply tw:border tw:border-default-200 tw:text-black tw:h-10;
}

.EdroneInput input::placeholder {
  /* Chrome, Firefox, Opera, Safari 10.1+ */
  @apply tw:text-default-600;
  opacity: 1; /* Firefox */
}

.EdroneInput input:-ms-input-placeholder {
  /* Internet Explorer 10-11 */
  @apply tw:text-default-600;
}

.EdroneInput input::-ms-input-placeholder {
  /* Microsoft Edge */
  @apply tw:text-default-600;
}

.EdroneInput input:hover {
  outline: none;
  @apply tw:border tw:border-default-400;
}

.EdroneInput input:focus {
  outline: none;
  @apply tw:border tw:border-blue-500;
}

.EdroneInput input:disabled {
  @apply tw:bg-default-100 tw:border tw:border-default-100;
}

.EdroneInput--invalid input {
  @apply tw:!border tw:!border-red-500;
}

.EdroneInput--invalid input:disabled {
  @apply tw:!border tw:!bg-red-000 tw:!text-red-500 tw:!border-red-000;
}

.EdroneInput input:focus {
  padding-right: 34px;
}

.EdroneInput--password input {
  padding-right: 34px;
}

.EdroneInput__icon-clear,
.EdroneInput__icon-password {
  display: none;
  position: absolute;
  top: 12px;
  right: 10px;
  font-size: 16px;
  cursor: pointer;
  width: 16px;
  height: 16px;
  user-select: none;
  @apply tw:text-black;
}

.EdroneInput__icon-password {
  display: block;
  @apply tw:text-default-300;
}

.EdroneInput--currency input {
  padding-left: 48px;
}

.EdroneInput__icon-currency {
  position: absolute;
  top: 1px;
  left: 1px;
  border-radius: 4px 0 0 4px;
  width: 39px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  @apply tw:bg-default-000 tw:text-default-700;
}

.EdroneInput:hover .EdroneInput__icon-clear {
  display: block !important;
}

.EdroneInput:hover .EdroneInput__icon-password {
  display: block !important;
}

.EdroneInput:hover:not(.EdroneInput--disabled) input {
  padding-right: 34px;
}

.EdroneInput input:focus + .EdroneInput__icon-clear {
  display: block !important;
}

.EdroneInput input:focus + .EdroneInput__icon-password {
  display: block !important;
}

.EdroneInput input:focus + .EdroneInput__icon-currency + .EdroneInput__icon-clear {
  display: block !important;
}

.EdroneInput input:focus + .EdroneInput__icon-currency {
  @apply tw:bg-blue-000 tw:text-blue-500;
}

.EdroneInput--invalid .EdroneInput__icon-currency {
  @apply tw:!text-red-500 tw:!bg-red-000;
}

.EdroneInput .EdroneInput__icon + input {
  padding-left: 2.5rem;
}

.EdroneInput .EdroneInput__icon {
  width: 2.5rem;
  height: 2.5rem;
  position: absolute;
  top: 0;
  left: 0;
  justify-content: center;
  align-items: center;
  display: flex;
  @apply tw:text-base tw:text-default-600;
}

.EdroneInput--filled .EdroneInput__icon {
  @apply tw:text-default-600;
}

.EdroneInput--filled .EdroneInput__icon-password {
  @apply tw:text-black;
}

.EdroneInput--disabled .EdroneInput__icon-currency {
  @apply tw:text-default-500 tw:bg-default-200;
}

.EdroneInput--disabled .EdroneInput__icon-password {
  @apply tw:text-default-300;
}

.EdroneInput--disabled input {
  @apply tw:text-default-800;
}

.EdroneInput--disabled input::placeholder {
  /* Chrome, Firefox, Opera, Safari 10.1+ */
  @apply tw:text-default-500;
  opacity: 1; /* Firefox */
}

.EdroneInput--disabled input:-ms-input-placeholder {
  /* Internet Explorer 10-11 */
  @apply tw:text-default-500;
}

.EdroneInput--disabled input::-ms-input-placeholder {
  /* Microsoft Edge */
  @apply tw:text-default-500;
}

.EdroneInput--disabled .EdroneInput__icon {
  @apply tw:text-default-300;
}

.EdroneInput--disabled .EdroneInput__clone-wrapper {
  pointer-events: auto;
}

.EdroneInput--disabled:hover .EdroneInput__icon-clear {
  display: none !important;
}

.EdroneInput--password .EdroneInput__icon-clear {
  display: none !important;
}

/* Chrome, Safari, Edge, Opera */
.EdroneInput input::-webkit-outer-spin-button,
.EdroneInput input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
.EdroneInput input[type="number"] {
  -moz-appearance: textfield;
}
</style>
